import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { LessonMap } from "@/components/LessonMap";
import { ErrorIconGrid } from "@/components/ErrorIconGrid";
import { findError } from "@/lib/errors";
import {
  ChevronDown, ChevronLeft, Navigation, Pause, MapPin, Flag, X, Check,
  Clock, Route as RouteIcon, Gauge, Zap, Camera, Repeat, Sparkles, Share2,
  Link as LinkIcon, Mail, MessageSquare, RotateCcw, ChevronRight,
} from "lucide-react";

const POS = [
  { x: "50%", y: "64%" },
  { x: "59%", y: "49%" },
  { x: "43%", y: "56%" },
  { x: "62%", y: "41%" },
];
const EXAMPLE = [findError("NP2L"), findError("MIRROR")];

const PHASES = [
  { key: "map", label: "Drop an error pin", phase: 0, icon: MapPin },
  { key: "icons", label: "Pick the category", phase: 1, icon: Flag },
  { key: "summary", label: "Lesson summary", phase: 2, icon: Clock },
  { key: "share", label: "Share the report", phase: 3, icon: Share2 },
];
const phaseOf = (s) => ({ map: 0, icons: 1, summary: 2, share: 3 }[s]);

const withPos = (list) => list.map((e, i) => ({ ...e, ...POS[i % POS.length] }));

export default function InteractiveDemo() {
  const [screen, setScreen] = useState("map");
  const [pins, setPins] = useState([]);

  const displayPins = pins.length ? pins : EXAMPLE;
  const go = (s) => setScreen(s);
  const pick = (err) => {
    setPins((prev) => (prev.length >= 4 ? prev : [...prev, err]));
    setScreen("map");
  };
  const restart = () => {
    setPins([]);
    setScreen("map");
  };

  return (
    <section id="demo" data-testid="demo-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
        {/* copy + steps */}
        <div className="lg:col-span-6">
          <Reveal>
            <p className="overline text-[#2BBFB3]">Try it yourself</p>
            <h2 className="mt-5 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.02] text-[#2B2B2B]">
              Step through a real lesson.
            </h2>
            <p className="mt-6 max-w-lg text-lg text-[#2B2B2B]/65 leading-relaxed">
              This is the actual DriveAssure flow. Drop an error pin on the map, choose the exact fault
              from the icon board, then review the report you'd share with a learner or parent.
            </p>
          </Reveal>

          <div className="mt-9 space-y-2.5">
            {PHASES.map((p, i) => {
              const active = phaseOf(screen) === p.phase;
              const done = phaseOf(screen) > p.phase;
              return (
                <Reveal key={p.key} delay={0.1 + i * 0.06}>
                  <button
                    data-testid={`demo-phase-${p.phase}`}
                    onClick={() => go(p.key)}
                    className={`group w-full flex items-center gap-4 rounded-2xl border p-4 text-left transition-colors ${
                      active ? "border-[#2BBFB3] bg-[#2BBFB3]/[0.06]" : "border-[#E5E5E5] bg-white hover:border-[#2B2B2B]/25"
                    }`}
                  >
                    <span className={`grid place-items-center w-10 h-10 rounded-xl shrink-0 transition-colors ${active || done ? "bg-[#2BBFB3] text-[#0f1211]" : "bg-[#2B2B2B]/5 text-[#2B2B2B]/50"}`}>
                      {done ? <Check size={18} strokeWidth={3} /> : <p.icon size={18} />}
                    </span>
                    <div className="flex-1">
                      <span className="text-xs font-semibold text-[#2B2B2B]/40">Step 0{p.phase + 1}</span>
                      <p className="font-display font-bold text-[#2B2B2B]">{p.label}</p>
                    </div>
                    <ChevronRight size={18} className={active ? "text-[#2BBFB3]" : "text-[#2B2B2B]/25 group-hover:translate-x-0.5 transition-transform"} />
                  </button>
                </Reveal>
              );
            })}
          </div>

          {pins.length > 0 && (
            <button
              data-testid="demo-restart-btn"
              onClick={restart}
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#2B2B2B]/60 hover:text-[#1FA097] transition-colors"
            >
              <RotateCcw size={15} /> Reset demo
            </button>
          )}
        </div>

        {/* phone */}
        <div className="lg:col-span-6 flex justify-center lg:justify-end">
          <Reveal delay={0.15}>
            <div className="relative rounded-[2.7rem] border-8 border-[#151614] bg-[#151614] shadow-2xl w-[300px] h-[620px] overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 z-30 w-28 h-6 bg-[#151614] rounded-b-2xl" />
              <div className="relative w-full h-full rounded-[2.1rem] overflow-hidden bg-white">
                <AnimatePresence mode="wait">
                  {screen === "map" && <MapScreen key="map" pins={pins} go={go} />}
                  {screen === "icons" && <IconsScreen key="icons" onPick={pick} go={go} />}
                  {screen === "summary" && <SummaryScreen key="summary" pins={displayPins} go={go} />}
                  {screen === "share" && <ShareScreen key="share" pins={displayPins} go={go} />}
                </AnimatePresence>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const fade = {
  initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 },
  transition: { duration: 0.3 },
};

/* ---------- 1. MAP (simulating lesson) ---------- */
function MapScreen({ pins, go }) {
  const placed = withPos(pins);
  return (
    <motion.div {...fade} className="absolute inset-0 flex flex-col bg-[#EDE7DC]">
      {/* status bar */}
      <div className="flex items-center justify-between px-5 pt-3 pb-1 text-[11px] font-bold text-[#1a1a1a]">
        <span className="flex items-center gap-1">19:55 <Navigation size={10} className="fill-[#1a1a1a]" /></span>
        <span className="text-[#1a1a1a]/70">▪▪▫ ▪ 􀛨</span>
      </div>
      {/* header card */}
      <div className="mx-3 mt-1 flex items-center gap-3 rounded-2xl bg-white/85 backdrop-blur px-3 py-2.5 shadow-sm">
        <span className="grid place-items-center w-8 h-8 rounded-full bg-white shadow-sm text-[#1a1a1a]"><ChevronDown size={16} /></span>
        <div className="flex-1 text-center">
          <p className="font-display font-extrabold text-[15px] text-[#1a1a1a] leading-none">James Sadler</p>
          <p className="text-[11px] font-semibold text-[#12A594] mt-0.5">Simulating</p>
        </div>
        <div className="text-center w-8">
          <p className="font-display font-extrabold text-xl text-[#12A594] leading-none" data-testid="demo-ie-count">{pins.length}</p>
          <p className="text-[9px] font-semibold text-[#1a1a1a]/50">IE</p>
        </div>
      </div>
      {/* stats */}
      <div className="mx-3 mt-2 grid grid-cols-3 rounded-2xl bg-white/85 backdrop-blur py-2.5 shadow-sm text-center">
        {[["127 m", "Distance"], ["0:05", "Time"], ["48 km/h", "Speed"]].map(([v, l], i) => (
          <div key={l} className={i < 2 ? "border-r border-[#1a1a1a]/10" : ""}>
            <p className="font-display font-extrabold text-[15px] text-[#1a1a1a] leading-none">{v}</p>
            <p className="text-[9px] text-[#1a1a1a]/45 mt-1">{l}</p>
          </div>
        ))}
      </div>
      {/* map */}
      <div className="relative flex-1 mt-2 mx-0">
        <LessonMap pins={placed.map((p, i) => ({ ...p, drop: i === placed.length - 1 }))} showCurrent />
      </div>
      {/* controls */}
      <div className="px-3 pb-3 pt-2 bg-[#EDE7DC]">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#1a1a1a] text-white px-3.5 py-2.5 text-[11px] font-bold"><Navigation size={13} /> Simulate</span>
          <span className="grid place-items-center w-12 h-12 rounded-full bg-[#1a1a1a] text-white ring-4 ring-white"><Pause size={18} className="fill-white" /></span>
          <button
            data-testid="demo-droppin-btn"
            onClick={() => go("icons")}
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#1a1a1a] px-3.5 py-2.5 text-[11px] font-bold shadow-sm hover:bg-[#f3f3f3] transition-colors"
          >
            <MapPin size={13} className="text-[#12A594]" /> Drop IE
          </button>
        </div>
        <button
          data-testid="demo-end-btn"
          onClick={() => go("summary")}
          className="mt-2.5 w-full rounded-full bg-[#1a1a1a] py-3 text-sm font-bold text-white hover:bg-black transition-colors"
        >
          End &amp; Review Lesson
        </button>
      </div>
    </motion.div>
  );
}

/* ---------- 2. ICON BOARD (pick error) ---------- */
function IconsScreen({ onPick, go }) {
  return (
    <motion.div {...fade} className="absolute inset-0 flex flex-col bg-white">
      <div className="flex items-center justify-between px-5 pt-3 pb-1 text-[11px] font-bold text-[#1a1a1a]">
        <span>19:55</span><span className="text-[#1a1a1a]/70">▪▪▫ ▪ 􀛨</span>
      </div>
      <div className="flex items-start justify-between px-4 pt-2 pb-3">
        <div>
          <h4 className="font-display font-extrabold text-[18px] text-[#1a1a1a] leading-tight">Mark an Identified Error</h4>
          <p className="text-[11px] text-[#2B2B2B]/45 mt-0.5">Tap an icon · swipe for more</p>
        </div>
        <button data-testid="demo-icons-close" onClick={() => go("map")} className="grid place-items-center w-9 h-9 rounded-full bg-[#F1F2F3] text-[#2B2B2B] shrink-0">
          <X size={17} strokeWidth={2.5} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 no-scrollbar">
        <ErrorIconGrid interactive onPick={onPick} />
      </div>
      <div className="flex items-center justify-center gap-1.5 py-3">
        <span className="w-5 h-1.5 rounded-full bg-[#12A594]" />
        <span className="w-1.5 h-1.5 rounded-full bg-[#2B2B2B]/20" />
      </div>
    </motion.div>
  );
}

/* ---------- 3. LESSON SUMMARY ---------- */
function SummaryScreen({ pins, go }) {
  const placed = withPos(pins);
  const stats = [
    { icon: Zap, v: pins.length, l: "Errors" },
    { icon: RouteIcon, v: "738 m", l: "Distance" },
    { icon: Clock, v: "0:32", l: "Time" },
    { icon: Gauge, v: "54 km/h", l: "Max" },
  ];
  return (
    <motion.div {...fade} className="absolute inset-0 flex flex-col bg-white">
      {/* header */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        <button onClick={() => go("map")} className="text-[#1a1a1a]"><ChevronLeft size={22} /></button>
        <div className="flex-1">
          <p className="font-display font-extrabold text-lg text-[#1a1a1a] leading-none">James Sadler</p>
          <p className="text-[11px] text-[#1a1a1a]/45 mt-1">10 Aug 2026 · 7:54 pm</p>
        </div>
        <button data-testid="demo-share-open" onClick={() => go("share")} className="grid place-items-center w-9 h-9 rounded-full bg-[#CFEDE8] text-[#0d7d70]">
          <Share2 size={16} />
        </button>
      </div>
      {/* map */}
      <div className="relative h-[150px]"><LessonMap pins={placed} /></div>
      {/* scroll body */}
      <div className="flex-1 overflow-y-auto no-scrollbar">
        <div className="grid grid-cols-4 gap-1 px-3 py-3">
          {stats.map((s) => (
            <div key={s.l} className="text-center">
              <s.icon size={15} className="mx-auto text-[#12A594]" />
              <p className="mt-1 font-display font-extrabold text-[15px] text-[#1a1a1a] leading-none">{s.v}</p>
              <p className="text-[9px] text-[#1a1a1a]/45 mt-0.5">{s.l}</p>
            </div>
          ))}
        </div>
        {/* summary for parents */}
        <div className="mx-3 rounded-2xl border border-[#BFE7E0] bg-[#F1FAF8] p-3.5">
          <p className="flex items-center gap-1.5 font-display font-bold text-[#0d7d70] text-sm"><Sparkles size={15} /> Summary for parents</p>
          <p className="mt-1.5 text-[11px] text-[#1a1a1a]/60 leading-relaxed">Generate a friendly plain-English summary of this lesson to include on shared links and PDFs.</p>
          <button onClick={() => go("share")} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white border border-[#BFE7E0] px-4 py-2 text-[12px] font-bold text-[#0d7d70]">
            <Sparkles size={13} /> Generate summary
          </button>
        </div>
        {/* identified errors */}
        <p className="mt-4 px-3 font-display font-extrabold text-[#1a1a1a]">Identified Errors</p>
        <div className="px-3 pt-2 pb-4 space-y-2">
          {placed.map((e, i) => (
            <div key={`${e.code}-${i}`} data-testid={`demo-summary-error-${e.code}`} className="rounded-2xl border border-[#EAEAEA] p-3">
              <div className="flex items-start gap-2">
                <span className="rounded-md px-2 py-1 text-[10px] font-extrabold text-white leading-none" style={{ background: e.color }}>{e.code}</span>
                <p className="flex-1 font-display font-bold text-[13px] text-[#1a1a1a] leading-tight">{e.full}</p>
                <span className="text-[11px] font-bold" style={{ color: e.color }}>{e.cat}</span>
                <span className="grid place-items-center w-7 h-7 rounded-full bg-[#CFEDE8] text-[#0d7d70]"><Camera size={13} /></span>
              </div>
              <p className="mt-1.5 text-[10px] text-[#1a1a1a]/45">◍ 1222 Greenhill Rd, Uraidla</p>
              {i === 0 && (
                <p className="mt-2 flex items-center gap-1.5 rounded-lg bg-[#FBF0D9] px-2 py-1.5 text-[10px] font-bold text-[#9A6A12]">
                  <Repeat size={12} /> Recurring spot — also flagged in 1 other lesson
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="px-3 pb-3 pt-2 border-t border-[#EEE]">
        <button data-testid="demo-signoff-btn" onClick={() => go("share")} className="w-full rounded-full bg-[#12A594] py-3 text-sm font-bold text-white hover:bg-[#0d7d70] transition-colors">
          Sign off Lesson
        </button>
      </div>
    </motion.div>
  );
}

/* ---------- 4. SHARE REPORT (parent/learner-facing) ---------- */
function ShareScreen({ pins, go }) {
  const focus = pins.map((p) => p.full).join(", ");
  return (
    <motion.div {...fade} className="absolute inset-0 flex flex-col bg-[#F4F6F5]">
      <div className="flex items-center gap-3 px-4 pt-4 pb-3 bg-white border-b border-[#EEE]">
        <button onClick={() => go("summary")} className="text-[#1a1a1a]"><ChevronLeft size={22} /></button>
        <p className="font-display font-extrabold text-[#1a1a1a]">Shareable report</p>
      </div>
      <div className="flex-1 overflow-y-auto no-scrollbar p-3">
        <div className="rounded-2xl bg-white border border-[#EAEAEA] overflow-hidden">
          {/* report header */}
          <div className="bg-[#1a1a1a] px-4 py-3.5">
            <span className="font-display font-extrabold text-[15px]"><span className="text-white">Drive</span><span className="text-[#2BBFB3]">Assure</span></span>
            <p className="mt-2 text-[10px] uppercase tracking-widest text-[#2BBFB3] font-bold">Progress report</p>
            <p className="font-display font-extrabold text-white text-lg leading-tight">James Sadler</p>
            <p className="text-[11px] text-white/55">10 Aug 2026 · Uraidla</p>
          </div>
          <div className="relative h-[110px]"><LessonMap pins={withPos(pins)} /></div>
          {/* stats */}
          <div className="grid grid-cols-3 px-3 py-3 text-center border-b border-[#EEE]">
            {[["738 m", "Distance"], ["0:32", "Time"], [`${pins.length}`, "Focus areas"]].map(([v, l], i) => (
              <div key={l} className={i < 2 ? "border-r border-[#EEE]" : ""}>
                <p className="font-display font-extrabold text-[15px] text-[#1a1a1a] leading-none">{v}</p>
                <p className="text-[9px] text-[#1a1a1a]/45 mt-1">{l}</p>
              </div>
            ))}
          </div>
          {/* plain english */}
          <div className="px-4 py-3">
            <p className="text-[10px] uppercase tracking-widest text-[#0d7d70] font-bold">Summary for parents</p>
            <p className="mt-1.5 text-[12px] text-[#1a1a1a]/70 leading-relaxed">
              James completed a 32-minute drive covering 738&nbsp;m, reaching 54&nbsp;km/h. We flagged{" "}
              <span className="font-semibold text-[#1a1a1a]">{pins.length} area{pins.length !== 1 ? "s" : ""}</span> to practise
              {focus ? `: ${focus.toLowerCase()}` : ""}. A confident drive overall — these are the spots to focus on before next lesson.
            </p>
          </div>
          {/* focus list */}
          <div className="px-4 pb-4 space-y-1.5">
            {pins.map((e, i) => (
              <div key={`${e.code}-${i}`} className="flex items-center gap-2.5 rounded-xl bg-[#F7F7F7] px-3 py-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: e.color }} />
                <p className="flex-1 text-[12px] font-semibold text-[#1a1a1a]">{e.full}</p>
                <span className="text-[10px] font-bold text-[#1a1a1a]/40">{e.cat}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* share actions */}
      <div className="bg-white border-t border-[#EEE] px-4 pt-3 pb-4">
        <button data-testid="demo-share-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#12A594] py-3 text-sm font-bold text-white hover:bg-[#0d7d70] transition-colors">
          <Share2 size={16} /> Share report
        </button>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {[[LinkIcon, "Copy link"], [Mail, "Email"], [MessageSquare, "Message"]].map(([Ic, l]) => (
            <div key={l} className="flex flex-col items-center gap-1 rounded-xl bg-[#F4F6F5] py-2.5">
              <Ic size={16} className="text-[#0d7d70]" />
              <span className="text-[10px] font-semibold text-[#1a1a1a]/60">{l}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
